import { Clock, Users, Star, AlertTriangle, MessageSquare } from 'lucide-react';
import clsx from 'clsx';
import type { Reservation, Guest } from '../../hooks/useShiftSetupData';

interface ReservationTimelineProps {
  reservations: Reservation[];
  guests: Guest[];
  shiftStartTime: string;
  shiftEndTime: string;
}

function formatTime(timeStr: string): string {
  // Handle HH:mm format
  if (timeStr.includes(':') && !timeStr.includes('T')) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
  // Handle ISO format
  const date = new Date(timeStr);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default function ReservationTimeline({
  reservations,
  guests,
  shiftStartTime,
  shiftEndTime,
}: ReservationTimelineProps) {
  // Sort reservations by time
  const sortedReservations = [...reservations].sort((a, b) => {
    const timeA = a.time || '';
    const timeB = b.time || '';
    return timeA.localeCompare(timeB);
  });

  // Get guest details by ID
  const getGuestDetails = (guestId?: string): Guest | undefined => {
    if (!guestId) return undefined;
    return guests.find((g) => g.id === guestId);
  };

  // Group reservations by time slot (30 min intervals)
  const groupedByTime: Record<string, Reservation[]> = {};
  for (const res of sortedReservations) {
    const time = res.time || 'Unknown';
    if (!groupedByTime[time]) {
      groupedByTime[time] = [];
    }
    groupedByTime[time].push(res);
  }

  const timeSlots = Object.keys(groupedByTime).sort();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wide">
          <Clock className="w-4 h-4" />
          Reservations
        </h3>
        <span className="text-xs text-slate-500">
          {formatTime(shiftStartTime)} - {formatTime(shiftEndTime)}
        </span>
      </div>

      <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
        {timeSlots.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No reservations during this shift</p>
          </div>
        ) : (
          timeSlots.map((time) => (
            <div key={time} className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-slate-700">
                  {formatTime(time)}
                </span>
                <span className="text-xs text-slate-400">
                  ({groupedByTime[time].length} reservation
                  {groupedByTime[time].length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="space-y-2">
                {groupedByTime[time].map((res) => {
                  const guest = getGuestDetails(res.guestId);
                  const isVIP = guest?.isVIP;
                  const hasAllergies = (guest?.allergies?.length || 0) > 0;
                  const hasNotes = !!res.notes;
                  const hasPreferences = guest?.preferences?.preferredServer;

                  return (
                    <div
                      key={res.id}
                      className={clsx(
                        'rounded-lg border p-3 transition-colors',
                        isVIP
                          ? 'border-purple-200 bg-purple-50/50'
                          : hasAllergies
                          ? 'border-amber-200 bg-amber-50/30'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 truncate">
                              {res.guestName}
                            </span>
                            {isVIP && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-purple-700 bg-purple-100 rounded">
                                <Star className="w-3 h-3" />
                                VIP
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Party of {res.partySize}
                            </span>
                            {res.tableId && <span>Table #{res.tableId.slice(-4)}</span>}
                            {!res.tableId && (res.tableIds?.length ?? 0) > 0 && (
                              <span>{res.tableIds!.length} table preference{res.tableIds!.length !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Guest preferences */}
                      {hasPreferences && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-indigo-600">
                          <Users className="w-3 h-3" />
                          <span>Prefers: {guest?.preferences?.preferredServer}</span>
                        </div>
                      )}

                      {/* Allergies */}
                      {hasAllergies && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>Allergies: {guest?.allergies?.join(', ')}</span>
                        </div>
                      )}

                      {/* Dietary restrictions */}
                      {(guest?.dietaryRestrictions?.length || 0) > 0 && (
                        <div className="mt-1 text-xs text-slate-500">
                          Dietary: {guest?.dietaryRestrictions?.join(', ')}
                        </div>
                      )}

                      {/* Notes */}
                      {hasNotes && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600">
                          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="italic">{res.notes}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
